import React from 'react';
import {isEmpty as _isEmpty} from 'lodash';
import {useRouteMatch} from 'react-router';
import {useParams} from 'react-router-dom';

import {convertToInt, convertSecondsToMinutesWithOneDecimal} from '@utils/helpers';
import {
    INCIDENT_REPORT_TYPE,
    NOT_STREAMED_REPORT_TYPE, WORKING_REPORT_TYPE,
} from '@constants/variables';
import {Icon, IconCheckmark, IconX} from '@appComponents/IconCollection';
import {routes} from '@constants/routes';
import {createForm, renderModalError} from '@utils/forms';
import {getLink} from '@appComponents/Link';
import {
    EventReportIncidentAddModel,
    EventReportIncidentEditModel,
} from '@modules/events/forms/EventReportIncidentModel';
import EventReportIncidentAddForm from '@modules/events/forms/EventReportIncidentAddForm';
import {EventReportBasicAddModel, EventReportBasicEditModel} from '@modules/events/forms/EventReportBasicModel';
import EventReportBasicForm from '@modules/events/forms/EventReportBasicForm';
import EventReportIncidentEditForm from '@modules/events/forms/EventReportIncidentEditForm';
import SetDurationForm from '@modules/events/forms/DurationForm';
import SetDurationModel from '@modules/events/forms/SetDurationModel';
import DistributionInvoiceStatusForm from '@modules/events/forms/DistributionInvoiceStatusForm';
import DistributionInvoiceStatusModel from '@modules/events/forms/DistributionInvoiceStatusModel';

export const useEventsReportRoutes = (reportStateData, reportsData, durationAndMatchData) => {
    const match = useRouteMatch();
    const {
        id: eventId,
        reportId,
    } = useParams();
    const duration = durationAndMatchData?.event?.duration ?? null;
    const durationInMinutesWithOneDecimal = convertSecondsToMinutesWithOneDecimal(duration);
    const matchData = durationAndMatchData?.event?.sr_event?.match ?? null;
    const matchStarted = matchData?.started_datetime ?? null;
    const matchEnded = matchData?.ended_datetime ?? null;
    const isReportsTableEmpty = _isEmpty(reportsData?.reports);
    const isEditingOnlyOwnReport = 1 === reportsData?.reports.length;
    const ALL_STREAMS_REPORTED = 'You cannot add a new report. All streams are already covered by a report.';
    const ALL_INCIDENTS_REPORTED = 'You cannot report a new incident. All streams are already reported as not streamed or working.';

    let Form;

    return (
        [
            {
                path: routes.events.reportInvoiceStatus.path,
                size: 'large',
                render: () => {
                    Form = createForm(
                        DistributionInvoiceStatusModel,
                        DistributionInvoiceStatusForm,
                        {
                            optionsVariables: {
                                event: convertToInt(eventId),
                            },
                        }
                    );

                    return <Form/>;
                },
            },
            {
                path: [
                    routes.events.reportIncidents.add.path,
                    routes.events.reportNotStreamed.add.path,
                    routes.events.reportWorking.add.path,
                ],
                render: () => {
                    let addType = null;

                    if (match.url.includes('add-incidents')) addType = INCIDENT_REPORT_TYPE;
                    else if (match.url.includes('add-not-streamed')) addType = NOT_STREAMED_REPORT_TYPE;
                    else if (match.url.includes('add-working')) addType = WORKING_REPORT_TYPE;

                    if (addType && false === reportStateData?.reportState?.hasReportableProducts) {
                        return renderModalError(
                            'Report',
                            getLink('events.report.index', {id: eventId}),
                            'This event does not contain any reportable distribution.'
                        );
                    }

                    switch (addType) {
                        case INCIDENT_REPORT_TYPE:
                            if (false === reportStateData?.reportState?.showIncidentButton) {
                                return renderModalError(
                                    'Incident',
                                    getLink('events.report.index', {id: eventId}),
                                    ALL_INCIDENTS_REPORTED
                                );
                            }
                            Form = createForm(
                                EventReportIncidentAddModel,
                                EventReportIncidentAddForm,
                                {
                                    event: convertToInt(eventId),
                                    reportId: null,
                                    duration: durationInMinutesWithOneDecimal,
                                    matchStarted,
                                    matchEnded,
                                }
                            );

                            return <Form/>;
                        case NOT_STREAMED_REPORT_TYPE:
                        case WORKING_REPORT_TYPE: {
                            const showButton = (addType === NOT_STREAMED_REPORT_TYPE)
                                ? reportStateData?.reportState?.showNotStreamedButton
                                : reportStateData?.reportState?.showWorkingButton;

                            if (
                                false === showButton ||
                                (false === reportStateData?.reportState.hasUncoveredCombinations)
                            ) {
                                return renderModalError(
                                    'Incident',
                                    getLink('events.report.index', {id: eventId}),
                                    ALL_STREAMS_REPORTED
                                );
                            }

                            Form = createForm(
                                () => EventReportBasicAddModel(addType),
                                EventReportBasicForm,
                                {
                                    event: convertToInt(eventId),
                                    reportId: null,
                                    reportType: addType,
                                    isReportsTableEmpty,
                                }
                            );

                            return <Form/>;
                        }
                    }
                },
            },
            {
                path: [
                    routes.events.reportIncidents.edit.path,
                    routes.events.reportNotStreamed.edit.path,
                    routes.events.reportWorking.edit.path,
                ],
                render: () => {
                    const currentReportType =
                        reportsData?.reports?.find(report => String(report.id) === String(reportId))?.report_type.id;

                    switch (currentReportType) {
                        case INCIDENT_REPORT_TYPE:
                            Form = createForm(
                                EventReportIncidentEditModel,
                                EventReportIncidentEditForm,
                                {
                                    event: convertToInt(eventId),
                                    id: convertToInt(reportId),
                                    reportId: convertToInt(reportId),
                                    duration: durationInMinutesWithOneDecimal,
                                    matchStarted,
                                    matchEnded,
                                }
                            );

                            return <Form/>;
                        case NOT_STREAMED_REPORT_TYPE:
                        case WORKING_REPORT_TYPE:
                            Form = createForm(
                                () => EventReportBasicEditModel(currentReportType),
                                EventReportBasicForm,
                                {
                                    event: eventId,
                                    id: convertToInt(reportId),
                                    reportType: currentReportType,
                                    isReportsTableEmpty,
                                    isEditingOnlyOwnReport,
                                }
                            );

                            return <Form/>;
                        default:
                            return renderModalError(
                                'Report',
                                getLink('events.report.index', {id: eventId})
                            );
                    }
                },
            },
            {
                path: routes.events.report.setEventDuration.path,
                render: () => {
                    const matchDurationData = matchData ? {
                        match: {
                            id: matchData.id,
                            match_status: {
                                name: matchData.match_status?.name,
                            },
                            started_datetime: matchData.started_datetime,
                            ended_datetime: matchData.ended_datetime,
                        },
                    } : null;

                    Form = createForm(
                        SetDurationModel,
                        SetDurationForm,
                        {
                            id: eventId,
                            dataRequest: false,
                            duration: durationInMinutesWithOneDecimal,
                            optionsVariables: {
                                event: eventId,
                                matchData: matchDurationData,
                            },
                        }
                    );

                    return <Form/>;
                },
            },
        ]
    );
};

export const getReportScopeLabel = (
    {has_full_coverage, has_no_coverage, has_partial_coverage, is_unreportable}={},
    {excel = false} = {}
) => {
    if (has_no_coverage) return excel ? 'None' : <IconX />;

    if (has_partial_coverage) return excel ? 'Partial' : <Icon name='minus' />;

    if (has_full_coverage) return excel ? 'Full' : <IconCheckmark />;

    if (is_unreportable) return excel ? 'Unreportable' : '';

    return '';
};
